import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import { ForecastTemplateColumns } from 'src/models/ForecastModels';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { logger } from 'src/analytics/KatalLogger';

/**
 * Service for validating non-editable fields in forecast templates
 */
export class NonEditableFieldsValidationService extends ValidationService {
  /**
   * Validates that non-editable fields have not been modified.
   * @param forecastTemplateRelevantRowData The array of data objects from the file.
   * @param forecastTemplateColumns The forecast template columns configuration.
   * @param forecastTemplateCompleteData The array of complete data objects from the forecast template.
   * @param isBudgetLeaderOrAdmin Flag indicating if the current user is a budget leader or admin.
   * @returns Promise with validation status
   */
  public async validateNonEditableFields(
    forecastTemplateRelevantRowData: any[],
    forecastTemplateColumns: ForecastTemplateColumns,
    forecastTemplateCompleteData: any[],
    isBudgetLeaderOrAdmin: boolean
  ): Promise<ValidationStatusEntity> {
    const validationErrorDetails: ValidationErrorDetail[] = [];

    logger.info(`Validating Non editable Fields for ${forecastTemplateRelevantRowData.length} records`);

    const mandatoryFieldsWithoutBudgetOwner = forecastTemplateColumns.mandatoryFields.filter(
      (field) => field !== ForecastGridFixedFields.BudgetOwner.value
    );

    const mandatoryFields = forecastTemplateColumns.mandatoryFields;

    // Create a map for quick lookup
    const completeDataMap = new Map<string, any>(
      forecastTemplateCompleteData.map((row) => [`${row[ForecastGridFixedFields.XptLineItemId.value]}`, row])
    );

    forecastTemplateRelevantRowData.forEach((row) => {
      const lineItemId = row[ForecastGridFixedFields.XptLineItemId.value];

      // If there is no Line Item Id, then it is new row, all fields are editable
      if (!lineItemId) {
        return;
      }

      // Determine which fields are non-editable based on row type and user role
      let nonEditableFieldsBasedOnRole: string[] = [];
      const isNewRow = row[ForecastGridFixedFields.IsNew.value];

      if (isNewRow) {
        // For new rows
        if (!isBudgetLeaderOrAdmin) {
          // Non-admin users can't edit budget owner on new rows
          nonEditableFieldsBasedOnRole = [ForecastGridFixedFields.BudgetOwner.value];
        } else {
          // Admins can edit all fields on new rows
          nonEditableFieldsBasedOnRole = [];
        }
      } else {
        // For existing rows
        if (!isBudgetLeaderOrAdmin) {
          // Non-admin users can't edit any mandatory fields on existing rows
          nonEditableFieldsBasedOnRole = mandatoryFields;
        } else {
          // Admins can edit budget owner but not other mandatory fields on existing rows
          nonEditableFieldsBasedOnRole = mandatoryFieldsWithoutBudgetOwner;
        }
      }

      // Check for modifications to non-editable fields
      const matchingRow = completeDataMap.get(`${lineItemId}`);
      if (matchingRow) {
        nonEditableFieldsBasedOnRole.forEach((field) => {
          if (row[field] !== matchingRow[field]) {
            validationErrorDetails.push({
              message: `${field} '${row[field]}' ${this.getLineItemIdMessagePart(
                row,
                ForecastGridFixedFields.XptLineItemId.value
              )} - Non-editable field`
            });
          }
        });
      }
    });

    // Log validation errors if any
    this.logValidationErrors('Non-editable Fields', validationErrorDetails);

    // Return appropriate validation status
    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(
        ForecastValidationMessages.NON_EDITABLE_FIELD_VALIDATION_FAILED,
        ForecastValidationMessages.NON_EDITABLE_FIELD_VALIDATION_DEFAULT,
        validationErrorDetails
      );
    }

    return this.createSuccessStatus(
      ForecastValidationMessages.NON_EDITABLE_FIELD_VALIDATION_SUCCESS,
      ForecastValidationMessages.NON_EDITABLE_FIELD_VALIDATION_DEFAULT
    );
  }
}

// Export singleton instance
export const nonEditableFieldsValidationService = new NonEditableFieldsValidationService();
